import type { ChangeEvent } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  SecondaryMenu,
  SecondaryMenuItemTitle,
  SecondaryMenuSectionTitle,
  SecondaryMenuTrigger,
} from 'components/editor/SidePanel/component';
import { useTranslation } from 'react-i18next';
import type { SelectChangeEvent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveSidePanel, EditorDefaultSidePanelType } from 'components/editor/grid/reduxStore/blockStyleSettingsSlice';
import { useTableManipulation } from 'components/editor/GridDndEditor/Block/Table/useTableManipulation';
import { SelectionContext } from 'components/editor/GridDndEditor/SelectedBlockInfoProvider';
import type { RootState } from '../../../grid/reduxStore/Store';
import { useTableContentUndoRedo } from '../../../hooks/useTableContentUndoRedo.ts';
import { selectContentTable, selectBlockContent } from '../../../grid/reduxStore/editorSlice';
import { TableTypeIdentifier } from '../../../grid/reduxStore/table.types';
import { useNewBlockDeletedHandler } from '../../../hooks/useNewBlockDeletedHandler';
import { useBlockStyleSettings } from '../../../hooks/useBlockSettings';
import { DEFAULT_DECIMAL_PLACES } from '../../../GridDndEditor/Block/Table/defaultData';
import { ContentType } from 'utils/getContentType';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectContentType } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { TableDefaultClassNamesEnum } from 'components/editor/grid/reduxStore/textDefaultStylingSlice.ts';

const DECIMAL_PLACES = ['0', '1', '2', '3', '4', '5'];

const TableDefaultSettings = () => {
  const contentType = useAppSelector(selectContentType);
  const { toggleTableType } = useTableManipulation();
  const { t } = useTranslation();
  const { handleTableContentChanged } = useTableContentUndoRedo();
  const dispatch = useDispatch();
  const { selectedBlockIdByWrapper, selectedSectionId } = useContext(SelectionContext);
  const sectionId = selectedSectionId as string;
  const blockId = selectedBlockIdByWrapper as string;
  const isToggleOn = useSelector((state: RootState) => state.blockStyleSettings.activeTotalToggle) || false;
  const tableData = useSelector((state: RootState) => selectContentTable(state, sectionId, blockId)) || undefined;
  const blockContent = useSelector((state: RootState) => selectBlockContent(state, sectionId, blockId)) || undefined;
  const [showTotals, setShowTotals] = useState<boolean>(isToggleOn);
  const [isToggleAvailable, setIsToggleAvailable] = useState<boolean>(true);
  const { setTableDecimalPlaces } = useTableManipulation();
  const [selectedDecimalPlaces, setSelectedDecimalPlaces] = useState<string>(
    String(tableData?.metadata?.pricingDecimalPlaces ?? DEFAULT_DECIMAL_PLACES)
  );
  const gridBlockDeletedHandler = useNewBlockDeletedHandler();
  const { deleteBlock } = useBlockStyleSettings();
  const isPricingTable = useMemo(() => tableData?.metadata?.tableType === TableTypeIdentifier.PRICING_TABLE, [tableData]);
  const pricingDecimalPlaces = useMemo(() => tableData?.metadata?.pricingDecimalPlaces, [tableData]);
  const isAllIdsAvailable = selectedBlockIdByWrapper && sectionId;
  useEffect(() => {
    if (tableData) {
      setShowTotals(isPricingTable);
      setSelectedDecimalPlaces(String(pricingDecimalPlaces));
    }
  }, [tableData, isPricingTable, pricingDecimalPlaces]);

  const handleDecimalPlacesChange = (event: SelectChangeEvent) => {
    const selectedValue = event.target.value;

    if (!isAllIdsAvailable || isNaN(Number(selectedValue))) return;
    setTableDecimalPlaces(selectedBlockIdByWrapper, sectionId, Number(selectedValue));
    setSelectedDecimalPlaces(selectedValue);
  };

  const handleShowTotalsChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setIsToggleAvailable(false);
      setShowTotals(event.target.checked);

      try {
        const updatedTableData = toggleTableType(blockId, sectionId);
        if (!updatedTableData || !blockContent) return;

        if (!updatedTableData.rows.length) {
          deleteBlock(blockId);
          gridBlockDeletedHandler(sectionId, blockId).then(() => {
            dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL }));
          });
          return;
        }

        handleTableContentChanged(blockId, sectionId, tableData, updatedTableData);
        setIsToggleAvailable(true);
      } catch (error) {
        setIsToggleAvailable(true);
        throw new Error('Error while updating table data');
      }
    },
    [blockId, sectionId, toggleTableType, handleTableContentChanged]
  );

  const handleBackButtonClick = () => {
    dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL }));
  };

  const handleDefaultStyleClick = () => {
    dispatch(
      setActiveSidePanel({ type: EditorDefaultSidePanelType.FORMAT_SETTINGS_PANEL, activeTextStyle: TableDefaultClassNamesEnum.TABLE_BODY })
    );
  };

  return (
    <SecondaryMenu
      testId={'table-default-settings'}
      menuTitle={t('document.grid.table.setting.table_settings')}
      backButtonOnClick={handleBackButtonClick}
    >
      <Stack rowGap={2} width="100%">
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.table.setting.pricing')}</SecondaryMenuSectionTitle>
        {contentType === ContentType.SECTION && (
          <Typography variant="body2" color="text.secondary">
            {t('document.grid.table.setting.section_currency_info')}
          </Typography>
        )}
        {/* Decimal Places */}
        <Grid container direction="column" rowGap={1}>
          <SecondaryMenuItemTitle>{t('document.grid.table.setting.decimal_places')}</SecondaryMenuItemTitle>
          <FormControl size="small" fullWidth>
            <Select data-testid="decimal-places-select" value={selectedDecimalPlaces} onChange={handleDecimalPlacesChange}>
              {DECIMAL_PLACES.map((place) => (
                <MenuItem key={place} value={place}>
                  {place}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Stack>
      {/* Show Totals */}
      <Grid container alignItems="center" justifyContent="space-between">
        <SecondaryMenuItemTitle>{t('document.grid.table.setting.show_table_footer')}</SecondaryMenuItemTitle>
        <Grid item>
          <Switch
            data-testid="pricing-toggle"
            disabled={!isToggleAvailable}
            inputProps={{ 'aria-label': 'show table footer' }}
            checked={showTotals}
            onChange={handleShowTotalsChange}
          />
        </Grid>
      </Grid>
      <SecondaryMenuTrigger
        ButtonText={t('document.grid.table.default_styles.edit_table_default_styles')}
        handleClick={handleDefaultStyleClick}
      />
    </SecondaryMenu>
  );
};

export default TableDefaultSettings;
